import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { InitialAssessment } from '@/components/InitialAssessment';
import { useAuth } from '@/context/auth/AuthContext';
import { supabase } from '@/lib/supabase';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { AssessmentComplete } from '@/components/assessment/AssessmentComplete';
import { generateLearningPath } from '@/lib/ai/learningPath';
import { useCareerPath } from '@/context/CareerContext';
import { useCareerPathDetails } from '@/context/CareerPathContext';

export function InitialAssessmentPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { careerPath } = useCareerPath(); // Gets the path they selected in Hero

  const handleAssessmentComplete = async (responses: any) => {
    if (!user?.id) return;
    
    setIsSubmitting(true);
    setError(null);

    try {
      // Save assessment responses
      const { error: responseError } = await supabase
        .from('assessment_responses')
        .insert({
          user_id: user.id,
          assessment_type: 'initial',
          responses: responses,
          completed_at: new Date().toISOString()
        });

      if (responseError) throw responseError;

      // Update user profile with assessment data
      const { error: profileError } = await supabase
        .from('user_profiles')
        .update({
          career_stage: responses.experience_level || 'entry',
          career_path: careerPath,
          career_info: {
            current_role: responses.currentRole,
            desired_role: responses.desiredRole,
            years_experience: responses.yearsExperience,
            preferred_work_type: responses.workPreference
          },
          skills_array: responses.skills || [],
          goals_array: responses.goals || [],
          onboarding_progress: {
            current_step: 'completed',
            completed_steps: ['assessment', 'career_path', 'skills', 'goals'],
            next_step: 'dashboard'
          },
          profile_completion: {
            basic_info: true,
            career_path: true,
            skills: true,
            goals: true
          },
          career_coins: 100, // Reward for completing assessment
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id);

      if (profileError) throw profileError;

      // Initialize career progress
      const { error: progressError } = await supabase
        .from('career_progress_overview')
        .insert({
          user_id: user.id,
          primary_career_path: careerPath,
          primary_path_match: '0%', // Initial match
          total_skills: responses.skills?.length || 0,
          latest_progress: 'Completed initial assessment'
        });

      if (progressError) throw progressError;

      // Navigate to dashboard with celebration state
      navigate('/dashboard', {
        state: { 
          showCelebration: true,
          onboardingComplete: true,
          earnedCoins: 100
        }
      });

    } catch (err) {
      console.error('Error completing assessment:', err);
      setError('Failed to complete assessment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      <InitialAssessment 
        careerPath={careerPath}
        onComplete={handleAssessmentComplete}
        isSubmitting={isSubmitting}
        error={error}
      />
    </div>
  );
}